import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const articleImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Chinese%20website%20registration%20page%20on%20laptop%20screen%20with%20clean%20modern%20design%2C%20warm%20lighting%2C%20minimal%20interface%2C%20professional%20atmosphere&width=800&height=400&seq=article-1&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=Password%20reset%20security%20concept%20on%20a%20modern%20website%20interface%2C%20warm%20neutral%20tones%2C%20clean%20UI%20design%2C%20professional%20tech%20atmosphere&width=800&height=400&seq=article-2&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Cybersecurity%20concept%20with%20shield%20icon%20on%20digital%20screen%2C%20warm%20professional%20atmosphere%2C%20clean%20modern%20design&width=800&height=400&seq=article-3&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=Digital%20platform%20features%20overview%20on%20a%20clean%20modern%20interface%2C%20warm%20professional%20design%2C%20multiple%20feature%20icons&width=800&height=400&seq=article-4&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Search%20engine%20optimization%20concept%20on%20a%20digital%20screen%2C%20warm%20lighting%2C%20clean%20modern%20interface%2C%20professional%20design&width=800&height=400&seq=article-5&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Official%20announcement%20on%20a%20website%20with%20megaphone%20icon%2C%20warm%20professional%20atmosphere%2C%20clean%20modern%20design&width=800&height=400&seq=article-6&orientation=landscape',
  'article-7': 'https://readdy.ai/api/search-image?query=Privacy%20policy%20and%20data%20protection%20concept%20on%20a%20digital%20screen%2C%20warm%20professional%20design%2C%20lock%20icon&width=800&height=400&seq=article-7&orientation=landscape',
  'article-8': 'https://readdy.ai/api/search-image?query=Terms%20of%20service%20document%20on%20a%20desk%20with%20warm%20lighting%2C%20professional%20legal%20atmosphere%2C%20clean%20modern%20design&width=800&height=400&seq=article-8&orientation=landscape',
  'article-9': 'https://readdy.ai/api/search-image?query=Troubleshooting%20guide%20concept%20on%20a%20laptop%20screen%2C%20warm%20professional%20atmosphere%2C%20question%20mark%20icon&width=800&height=400&seq=article-9&orientation=landscape',
  'article-10': 'https://readdy.ai/api/search-image?query=Phishing%20website%20warning%20concept%20with%20danger%20icon%20on%20digital%20screen%2C%20warm%20professional%20atmosphere&width=800&height=400&seq=article-10&orientation=landscape',
  'article-11': 'https://readdy.ai/api/search-image?query=Personalization%20settings%20interface%20on%20a%20modern%20website%2C%20warm%20professional%20design%2C%20clean%20and%20minimal&width=800&height=400&seq=article-11&orientation=landscape',
  'article-12': 'https://readdy.ai/api/search-image?query=Email%20verification%20concept%20on%20a%20laptop%2C%20warm%20professional%20atmosphere%2C%20envelope%20icon%2C%20modern%20design&width=800&height=400&seq=article-12&orientation=landscape',
};

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const article = articles.find((a) => a.id === articleId);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  const category = useMemo(() => {
    if (!article) return null;
    return categories.find((c) => c.id === article.category);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h2 className="mt-4 text-xl font-semibold text-foreground-950">文章未找到</h2>
            <p className="mt-2 text-sm text-foreground-500">该文章可能已被移除或地址不正确</p>
            <Link to="/" className="mt-6 inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-md text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const schemaArticle = {
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: { '@type': 'Person', name: article.author },
    publisher: { '@type': 'Organization', name: '91n.com' },
    image: articleImages[article.id],
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://91n.com/article/${article.id}` },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${article.title} - 91n.com`}
        description={article.excerpt}
        keywords={`91n.com,${article.title},91n.com教程,91n.com指南`}
        canonicalUrl={`/article/${articleId}`}
        ogType="article"
        ogImage={articleImages[article.id]}
      />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{ name: article.title, description: article.excerpt }} />
      <SchemaOrg type="Article" data={schemaArticle} />
      <SchemaOrg type="BreadcrumbList" />

      <Navbar />

      <main className="pt-16">
        <article className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb />

          <div className="max-w-4xl mx-auto">
            <div className="h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-8">
              <img
                src={articleImages[article.id]}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {category && (
              <Link
                to={`/category/${category.id}`}
                className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-500 hover:bg-primary-100 transition-colors mb-4 whitespace-nowrap cursor-pointer"
              >
                {category.name}
              </Link>
            )}

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> 阅读约 {article.readTime}
              </span>
            </div>

            <div className="mt-8 article-content text-foreground-700 leading-relaxed text-sm md:text-base [&>p]:mb-4 [&>h2]:text-xl [&>h2]:md:text-2xl [&>h2]:font-bold [&>h2]:text-foreground-950 [&>h2]:mt-8 [&>h2]:mb-4 [&>h2]:font-heading [&>ul]:mb-4 [&>ul]:pl-5 [&>ul]:space-y-1.5 [&>li]:list-disc [&>li]:text-foreground-600 [&>strong]:text-foreground-950 [&>strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h3 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
                相关文章推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all group cursor-pointer"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={articleImages[ra.id]}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                        <span>{ra.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i> {ra.readTime}
                        </span>
                      </div>
                      <h4 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                        {ra.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}